import { config } from '../config'

export interface AuthUser {
  username: string
  email: string
  sub: string
  groups: string[]
  isAdmin: boolean
}

export interface AuthTokens {
  idToken: string
  accessToken: string
  refreshToken: string
}

// Get the Hosted UI login URL
export function getLoginUrl(): string {
  const domain = config.cognito.domain
  const clientId = config.cognito.userPoolClientId
  const redirectUri = encodeURIComponent(config.oauth.redirectSignIn)
  const responseType = 'code'
  const scope = encodeURIComponent('email openid profile')

  return `${domain}/login?client_id=${clientId}&response_type=${responseType}&scope=${scope}&redirect_uri=${redirectUri}`
}

// Get the Hosted UI logout URL
export function getLogoutUrl(): string {
  const domain = config.cognito.domain
  const clientId = config.cognito.userPoolClientId
  const logoutUri = encodeURIComponent(config.oauth.redirectSignOut)

  return `${domain}/logout?client_id=${clientId}&logout_uri=${logoutUri}`
}

// Exchange authorization code for tokens
export async function exchangeCodeForTokens(code: string): Promise<AuthTokens> {
  const domain = config.cognito.domain
  const clientId = config.cognito.userPoolClientId
  const redirectUri = config.oauth.redirectSignIn

  const response = await fetch(`${domain}/oauth2/token`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: clientId,
      code: code,
      redirect_uri: redirectUri,
    }),
  })

  if (!response.ok) {
    throw new Error('Failed to exchange code for tokens')
  }

  const data = await response.json()
  return {
    idToken: data.id_token,
    accessToken: data.access_token,
    refreshToken: data.refresh_token,
  }
}

// Parse JWT token
function parseJwt(token: string): Record<string, unknown> {
  const base64Url = token.split('.')[1]
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
  const jsonPayload = decodeURIComponent(
    atob(base64)
      .split('')
      .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
      .join('')
  )
  return JSON.parse(jsonPayload)
}

// Get user from tokens
export function getUserFromTokens(tokens: AuthTokens): AuthUser {
  const payload = parseJwt(tokens.idToken)
  const groups = (payload['cognito:groups'] as string[]) || []

  return {
    username: payload['cognito:username'] as string,
    email: payload['email'] as string,
    sub: payload['sub'] as string,
    groups: groups,
    isAdmin: groups.includes('admin'),
  }
}

// Store tokens in session storage
export function storeTokens(tokens: AuthTokens): void {
  sessionStorage.setItem('idToken', tokens.idToken)
  sessionStorage.setItem('accessToken', tokens.accessToken)
  sessionStorage.setItem('refreshToken', tokens.refreshToken)
}

// Get tokens from session storage
export function getStoredTokens(): AuthTokens | null {
  const idToken = sessionStorage.getItem('idToken')
  const accessToken = sessionStorage.getItem('accessToken')
  const refreshToken = sessionStorage.getItem('refreshToken')

  if (!idToken || !accessToken || !refreshToken) {
    return null
  }

  return { idToken, accessToken, refreshToken }
}

// Clear tokens from session storage
export function clearTokens(): void {
  sessionStorage.removeItem('idToken')
  sessionStorage.removeItem('accessToken')
  sessionStorage.removeItem('refreshToken')
}

// Check if token is expired
export function isTokenExpired(token: string): boolean {
  try {
    const payload = parseJwt(token)
    const exp = payload['exp'] as number
    return Date.now() >= exp * 1000
  } catch {
    return true
  }
}

// Get current authenticated user
export function getCurrentUser(): AuthUser | null {
  const tokens = getStoredTokens()
  if (!tokens) {
    return null
  }

  if (isTokenExpired(tokens.idToken)) {
    clearTokens()
    return null
  }

  return getUserFromTokens(tokens)
}

// Get access token for API calls
export function getAccessToken(): string | null {
  const tokens = getStoredTokens()
  if (!tokens) {
    return null
  }

  if (isTokenExpired(tokens.accessToken)) {
    clearTokens()
    return null
  }

  return tokens.accessToken
}

// Get ID token for API calls (preferred for API Gateway with Cognito authorizer)
export function getIdToken(): string | null {
  const tokens = getStoredTokens()
  if (!tokens) {
    return null
  }

  if (isTokenExpired(tokens.idToken)) {
    clearTokens()
    return null
  }

  return tokens.idToken
}

// Logout user
export function logout(): void {
  clearTokens()
  window.location.href = getLogoutUrl()
}

// Redirect to login
export function redirectToLogin(): void {
  window.location.href = getLoginUrl()
}
